import { useState, useEffect, useCallback } from 'react';

interface CacheOptions {
  maxAge?: number; // en millisecondes
  staleWhileRevalidate?: boolean;
}

interface CachedData<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  isStale: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook pour gérer les données avec cache intelligent
 */
export function useCachedData<T>(
  key: string,
  fetcher: (() => Promise<T>) | undefined,
  options: CacheOptions = {}
): CachedData<T> {
  const { maxAge = 5 * 60 * 1000, staleWhileRevalidate = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(typeof fetcher === 'function');
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);

  const getCacheKey = useCallback(() => `cached_data_${key}`, [key]);
  const getTimestampKey = useCallback(() => `cached_timestamp_${key}`, [key]);

  const loadFromCache = useCallback((): T | null => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      const timestamp = localStorage.getItem(getTimestampKey());

      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        const parsedData = JSON.parse(cached);

        if (age < maxAge) {
          setIsStale(false);
          return parsedData;
        } else if (staleWhileRevalidate) {
          setIsStale(true);
          return parsedData;
        }
      }
    } catch {
      // Silently fail in production
    }

    return null;
  }, [getCacheKey, getTimestampKey, maxAge, staleWhileRevalidate]);

  const saveToCache = useCallback(
    (value: T) => {
      try {
        localStorage.setItem(getCacheKey(), JSON.stringify(value));
        localStorage.setItem(getTimestampKey(), Date.now().toString());
      } catch {
        // Silently fail in production
      }
    },
    [getCacheKey, getTimestampKey]
  );

  const fetchData = useCallback(
    async (showLoading = true) => {
      if (typeof fetcher !== 'function') {
        setLoading(false);
        return;
      }
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await fetcher();
        setData(result);
        setIsStale(false);
        saveToCache(result);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [fetcher, saveToCache]
  );

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (typeof fetcher !== 'function') {
      setLoading(false);
      return;
    }

    const cachedData = loadFromCache();

    if (cachedData) {
      setData(cachedData);
      setLoading(false);

      // Revalider en arrière-plan si stale
      if (isStale && staleWhileRevalidate) {
        fetchData(false);
      }
    } else {
      fetchData(true);
    }
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, isStale, refetch };
}

/**
 * Hook pour invalider le cache
 */
export function useInvalidateCache() {
  return useCallback((key: string) => {
    try {
      localStorage.removeItem(`cached_data_${key}`);
      localStorage.removeItem(`cached_timestamp_${key}`);
    } catch {
      // Silently fail in production
    }
  }, []);
}

/**
 * Hook pour obtenir les stats du cache
 */
export function useCacheStats() {
  const [stats, setStats] = useState<{
    [key: string]: { count: number; size: number };
  } | null>(null);

  const fetchStats = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      if (registration.active) {
        const messageChannel = new MessageChannel();
        
        messageChannel.port1.onmessage = (event) => {
          setStats(event.data);
        };

        registration.active.postMessage(
          { type: 'GET_CACHE_STATS' },
          [messageChannel.port2]
        );
      }
    } catch {
      // Silently fail in production
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, refetch: fetchStats };
}